// Firestore repository for complete Silent Auction demos.
// Seeds production-shaped items, private/redacted bid history, multi-unit
// allocations, sample Store Orders, event timers, and linked Entry metadata.

import { collection, doc, writeBatch, Timestamp } from "firebase/firestore";
import { db } from "../../../firebase";

export const saveAuctionItems = async ({ demoContext, items = [], bids = [] } = {}) => {
  const eventId = demoContext?.eventId || demoContext?.eventResult?.eventId || demoContext?.event?.eventId;
  if (!eventId) throw new Error("Auction Repository requires an eventId.");
  if (!items.length) return { items: [], bids: [], allocations: [], orders: [] };

  const now = Date.now();
  const batch = writeBatch(db);
  const savedItems = [];
  const savedBids = [];
  const allocations = [];
  const orders = [];

  batch.set(doc(db, "events", eventId), {
    // Demo orders are synthetic. Never request or charge a real saved card.
    auctionRegistrationRequired: false,
    auctionSmsNotificationsEnabled: false,
    auctionStart: Timestamp.fromDate(new Date(now - 86400000)),
    auctionEnd: Timestamp.fromDate(new Date(now + (7 * 86400000))),
  }, { merge: true });

  items.forEach((plannedItem) => {
    const itemRef = doc(collection(db, "events", eventId, "auctionItems"));
    const itemBids = bids.filter((bid) => bid.itemDemoKey === plannedItem.demoKey)
      .map((bid, index) => ({ ...bid, sequenceNumber: index + 1 }));
    const rankedBids = [...itemBids].sort((a, b) => b.amountCents - a.amountCents);
    const uniqueBidders = new Set();
    const winners = rankedBids.filter((bid) => {
      if (uniqueBidders.has(bid.bidderUid)) return false;
      uniqueBidders.add(bid.bidderUid);
      return true;
    }).slice(0, plannedItem.quantityAvailable || 1);
    const settlementPriceCents = winners.length ? Math.min(...winners.map((bid) => bid.amountCents)) : 0;
    const leader = rankedBids[0];
    const { demoKey, ...item } = plannedItem;
    const closed = item.status === "closed";
    const itemAuctionStart = item.auctionStart ? new Date(item.auctionStart) : item.status === "draft"
      ? new Date(now + 86400000)
      : item.status === "open" ? new Date(now - 86400000) : new Date(now - (7 * 86400000));
    const itemAuctionEnd = item.auctionEnd ? new Date(item.auctionEnd) : item.status === "draft"
      ? new Date(now + (8 * 86400000))
      : item.status === "open" ? new Date(now + (5 * 86400000)) : new Date(now - 86400000);
    const savedItem = {
      ...item,
      eventId,
      currentBid: leader ? leader.amountCents / 100 : 0,
      currentBidCents: leader?.amountCents || 0,
      currentWinnerUid: leader?.bidderUid || "",
      currentWinnerName: leader?.bidderName || "",
      currentWinnerEmail: leader?.bidderEmail || "",
      currentWinnerCode: leader?.bidderCode || "",
      auctionStart: Timestamp.fromDate(itemAuctionStart),
      auctionEnd: Timestamp.fromDate(itemAuctionEnd),
      bidCount: itemBids.length,
      unitsAwarded: closed ? winners.length : 0,
      ...(closed ? { allocationCount: winners.length, settlementPriceCents,
        pricingMethod: "lowest_winning_bid", winningBidSequence: leader?.sequenceNumber || null } : {}),
      allocationFinalized: closed,
      createdAt: Timestamp.fromDate(new Date(now - ((item.displayOrder || 10) * 60000))),
      updatedAt: Timestamp.fromDate(new Date(now - 30000)),
      demoSeed: true,
    };
    batch.set(itemRef, savedItem);

    if (item.linkedEntryId) {
      batch.set(doc(db, "events", eventId, "cars", item.linkedEntryId), {
        isAuctionItem: true,
        auctionItemId: itemRef.id,
      }, { merge: true });
    }
    savedItems.push({ id: itemRef.id, ...savedItem });

    itemBids.forEach((bid, bidIndex) => {
      const bidRef = doc(collection(db, "events", eventId, "auctionBids"));
      const createdAt = Timestamp.fromDate(new Date(now - ((itemBids.length - bidIndex) * 120000)));
      const privateBid = {
        itemId: itemRef.id,
        bidderUid: bid.bidderUid,
        bidderName: bid.bidderName,
        bidderEmail: bid.bidderEmail,
        bidderCode: bid.bidderCode,
        amount: bid.amountCents / 100,
        amountCents: bid.amountCents,
        sequenceNumber: bid.sequenceNumber,
        createdAt,
        immutable: true,
        demoSeed: true,
      };
      batch.set(bidRef, privateBid);
      batch.set(doc(db, "events", eventId, "auctionBidActivity", bidRef.id), {
        itemId: itemRef.id,
        bidId: bidRef.id,
        bidderLabel: bid.bidderCode,
        bidderCode: bid.bidderCode,
        amount: bid.amountCents / 100,
        amountCents: bid.amountCents,
        sequenceNumber: bid.sequenceNumber,
        createdAt,
        demoSeed: true,
      });
      savedBids.push({ id: bidRef.id, ...privateBid });
    });

    if (closed) {
      winners.forEach((winner, winnerIndex) => {
        const allocationRef = doc(collection(itemRef, "allocations"));
        const orderRef = doc(collection(db, "events", eventId, "orders"));
        const paid = winnerIndex === 0;
        const platformFeeCents = Math.round(settlementPriceCents * 0.01);
        const allocation = {
          itemId: itemRef.id,
          rank: winnerIndex + 1,
          quantity: 1,
          bidderUid: winner.bidderUid,
          bidderName: winner.bidderName,
          bidderEmail: winner.bidderEmail,
          bidderCode: winner.bidderCode,
          amount: settlementPriceCents / 100,
          amountCents: settlementPriceCents,
          platformFeeCents,
          platformFeeRateBps: 100,
          winningBidAmountCents: winner.amountCents,
          bidSequence: winner.sequenceNumber,
          orderId: orderRef.id,
          paymentStatus: paid ? "paid" : "unpaid",
          ...(paid ? {} : { autoPaymentStatus: "requires_payment" }),
          pickupStatus: paid ? "awaiting_pickup" : "awaiting_payment",
          createdAt: Timestamp.fromDate(new Date(now - 20000)),
          demoSeed: true,
        };
        batch.set(allocationRef, allocation);
        allocations.push({ id: allocationRef.id, ...allocation });

        const order = {
          orderType: "auction",
          provider: "demo",
          demoSeed: true,
          status: paid ? "paid" : "pending",
          ...(paid ? {} : { autoPaymentStatus: "requires_payment", autoPaymentErrorCode: "demo_payment_recovery" }),
          fulfillmentStatus: paid ? "ready_for_pickup" : "awaiting_payment",
          eventId,
          auctionItemId: itemRef.id,
          auctionAllocationId: allocationRef.id,
          auctionItemTitle: item.title,
          amountCents: settlementPriceCents,
          totalAmount: settlementPriceCents,
          platformFeeCents,
          platformFeeRateBps: 100,
          bidSequence: winner.sequenceNumber,
          bidderUid: winner.bidderUid,
          bidderName: winner.bidderName,
          bidderEmail: winner.bidderEmail,
          bidderCode: winner.bidderCode,
          uid: winner.bidderUid,
          buyerEmail: winner.bidderEmail,
          demoWinnerEmailStatus: "simulated_sent",
          demoWinnerEmailReason: "Auction winner notice",
          createdAt: Timestamp.fromDate(new Date(now - 10000)),
          updatedAt: Timestamp.fromDate(new Date(now - 10000)),
        };
        batch.set(orderRef, order);
        orders.push({ id: orderRef.id, ...order });
      });
    }
  });

  await batch.commit();
  return { items: savedItems, bids: savedBids, allocations, orders };
};
